// src/App.tsx
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { LoadingScreen } from '@/components/common/LoadingSpinner';
import { MainLayout } from '@/components/layout/MainLayout';
import NavigationInjector from '@/components/common/NavigationInjector';
import { ToastProvider } from '@/contexts/ToastContext';
import ScrollToTop from '@/components/common/ScrollToTop';
import { SplashScreen } from '@/components/common/SplashScreen';

// Auth pages
import LoginPage from '@/pages/auth/LoginPage';
import SignupPage from '@/pages/auth/SignupPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import AuthCallback from '@/pages/auth/CallbackPage';

// Main pages
import { HomePage } from '@/pages/dashboard/HomePage';
import { FeedPage } from '@/pages/feed/FeedPage';
import { ReelsPage } from '@/pages/feed/ReelsPage';
import { SearchPage } from '@/pages/dashboard/SearchPage';
import MessagesPage from '@/pages/chat/ChatPage';
import { ProfilePage } from '@/pages/profile/ProfilePage';
import { MapPage } from '@/pages/dashboard/MapPage';
import { SettingsPage } from '@/pages/settings/SettingsPage';

// Protected Route wrapper
// Protected Route wrapper
interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  requiredPortal?: 'business' | 'landlord' | 'staff' | 'admin';
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles = [],
  requiredPortal
}) => {
  const { user, loading, hasRole, canAccessPortal } = useAuth();

  // While auth is still initialising (session restore on reload), show loading.
  // Never redirect until loading is fully done — avoids the flash-to-login bug.
  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0) {
    const hasRequiredRole = allowedRoles.some(role => hasRole(role as any));
    if (!hasRequiredRole) return <Navigate to="/" replace />;
  }

  if (requiredPortal) {
    if (!canAccessPortal(requiredPortal)) return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Business & Landlord pages

import { BusinessDashboard } from '@/pages/business/BusinessDashboard';
import { BusinessSelectionPage } from '@/pages/business/BusinessSelectionPage';
import { BusinessRegistrationPage } from '@/pages/business/BusinessRegistrationPage';
import EditBusinessProfilePage from '@/pages/business/EditBusinessProfilePage';
import BusinessDetailsPage from '@/pages/business/BusinessDetailsPage';
import { ProductList } from '@/pages/business/products/ProductList';
import { ProductForm } from '@/pages/business/products/ProductForm';
import { ProductDetails } from '@/pages/business/products/ProductDetails';
import { ServiceList } from '@/pages/business/services/ServiceList';
import { ServiceForm } from '@/pages/business/services/ServiceForm';
import { BarcodeManager } from '@/pages/business/BarcodeManager';
import { ReportsPage } from '@/pages/business/ReportsPage';
import { StaffPage } from '@/pages/business/StaffPage';
import { BusinessOrdersPage } from '@/pages/business/orders/BusinessOrdersPage';
import { MpesaDashboard } from '@/pages/business/mpesa/MpesaDashboard';
import MpesaSetupPage from '@/pages/business/mpesa/MpesaSetupPage';
import InventoryManagementPage from '@/pages/business/inventory/InventoryManagementPage';
import BusinessChatListPage from '@/pages/business/chats/BusinessChatListPage';
import PaymentsPage from '@/pages/business/payments/PaymentsPage';
import CustomerReviewsPage from '@/pages/business/reviews/CustomerReviewsPage';
import { SalesHistoryPage } from '@/pages/business/sales/SalesHistoryPage';
import { RecordSalePage } from '@/pages/business/sales/RecordSalePage';
import { BusinessHoursPage } from '@/pages/business/settings/BusinessHoursPage';
import BookingsPage from '@/pages/business/bookings/BookingsPage';
import BookingDetails from '@/pages/business/bookings/BookingDetails';
import NewBooking from '@/pages/business/bookings/NewBooking';
import ServiceDetails from '@/pages/business/services/ServiceDetails';
import PromotionsPage from '@/pages/business/marketing/PromotionsPage';
import CommissionApproval from '@/pages/business/commission/CommissionApproval';
import CommissionPayout from '@/pages/business/commission/CommissionPayout';
import BusinessPosts from '@/pages/business/posts/BusinessPosts';
import BusinessPostDetail from '@/pages/business/posts/BusinessPostDetail';
import PostOffer from '@/pages/business/posts/PostOffer';
import ProductJourney from '@/pages/business/products/ProductJourney';
import CommissionTasks from '@/pages/business/commission/CommissionTasks';
import NotificationsPage from '@/pages/business/notifications/NotificationsPage';
import AdvancedSearchPage from '@/pages/business/search/AdvancedSearchPage';
import CartPage from '@/pages/cart/CartPage';
import PosCartPage from '@/pages/business/CartPage';
import LivePOSPage from '@/pages/business/LivePOSPage';
import PricingPage from '@/pages/subscriptions/PricingPage';
import PublicProfile from '@/pages/business/public/PublicProfile';
import { LandlordLayout } from '@/components/layout/LandlordLayout';
import { LandlordDashboard } from '@/pages/landlord/LandlordDashboard';
import { PropertyList } from '@/pages/landlord/properties/PropertyList';

import { PropertyDetails } from './pages/landlord/properties/details/PropertyDetails';
import { TenantList } from '@/pages/landlord/tenants/TenantList';
import TenantDetails from '@/pages/landlord/tenants/details/TenantDetails';
import RegisterTenant from '@/pages/landlord/tenants/RegisterTenant';
import { Financials } from '@/pages/landlord/financials/Financials';
import { MaintenanceList } from '@/pages/landlord/maintenance/MaintenanceList';
import { LandlordBookings } from '@/pages/landlord/bookings/LandlordBookings';
import { UtilitiesManager } from '@/pages/landlord/utilities/UtilitiesManager';
import { VacancyList } from '@/pages/landlord/vacancies/VacancyList';
import { PostVacancy } from '@/pages/landlord/vacancies/PostVacancy';
import { VacancyPreview } from '@/pages/landlord/vacancies/VacancyPreview';
import { EditVacancy } from '@/pages/landlord/vacancies/EditVacancy';
import { LandlordReport } from '@/pages/landlord/reports/LandlordReport';
import { PropertyPerformance } from '@/pages/landlord/reports/PropertyPerformance';
import { OccupancyAnalytics } from '@/pages/landlord/reports/OccupancyAnalytics';
import { MaintenanceDetails } from '@/pages/landlord/maintenance/MaintenanceDetails';
import { BookingDetails as LandlordBookingDetails } from '@/pages/landlord/bookings/BookingDetails';
import { PropertyStaffManagement } from '@/pages/landlord/staff/PropertyStaffManagement';
import DocumentManager from '@/pages/landlord/documents/DocumentManager';
import PortfolioOverview from '@/pages/landlord/portfolio/PortfolioOverview';
import { LandlordStaff } from '@/pages/landlord/staff/LandlordStaff';
import { AddStaff } from '@/pages/landlord/staff/AddStaff';
import { EditStaff } from '@/pages/landlord/staff/EditStaff';
import { StaffDetails } from '@/pages/landlord/staff/StaffDetails';
import StaffPermissions from '@/pages/landlord/staff/StaffPermissions';

// Projects
import { ProjectList } from '@/pages/landlord/projects/ProjectList';
import { CreateProject } from '@/pages/landlord/projects/CreateProject';
import { EditProject } from '@/pages/landlord/projects/EditProject';
import { ProjectDetails } from '@/pages/landlord/projects/ProjectDetails';
import { PropertyForm } from '@/pages/landlord/properties/PropertyForm';
import { EditProperty } from '@/pages/landlord/properties/EditProperty';
import ManageUnits from '@/pages/landlord/units/ManageUnits';
import AllUnits from '@/pages/landlord/units/AllUnits';
import { UnitDetails } from '@/pages/landlord/units/UnitDetails';
import { UnitRegistration } from '@/pages/landlord/units/UnitRegistration';
import { CreateInvoice } from '@/pages/landlord/financials/CreateInvoice';
import RecordPayment from '@/pages/landlord/financials/RecordPayment';
import AutomatedBilling from '@/pages/landlord/financials/AutomatedBilling';
import { ManagePaymentMethods } from '@/pages/landlord/financials/ManagePaymentMethods';
import { TumaSetup } from '@/pages/landlord/financials/TumaSetup';
import { ManageBillingSchedules } from '@/pages/landlord/financials/ManageBillingSchedules';
import { CreateBillingSchedule } from '@/pages/landlord/financials/CreateBillingSchedule';

import { TenantLayout } from '@/components/layout/TenantLayout';
import { TenantDashboard } from '@/pages/tenant/TenantDashboard';
import { TenantProfile } from '@/pages/tenant/TenantProfile';
import { MyPayments } from '@/pages/tenant/MyPayments';
import { MyMaintenance } from '@/pages/tenant/MyMaintenance';
import { MyDocuments } from '@/pages/tenant/MyDocuments';
import { TenancySelector } from '@/pages/tenant/TenancySelector';
import { PaymentLinksPage } from '@/pages/business/PaymentLinksPage';

// ... existing imports ...

// One-time redirect to active role portal from root on initial boot
const BootRedirect: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile, loading } = useAuth();
  
  if (loading) return <LoadingScreen />;
  
  if (profile && !sessionStorage.getItem('majengo_has_booted')) {
    sessionStorage.setItem('majengo_has_booted', 'true');
    const role = profile.active_role?.toLowerCase() || '';
    if (role.includes('business') || role.includes('staff')) return <Navigate to="/business" replace />;
    if (role.includes('landlord')) return <Navigate to="/landlord" replace />;
    if (role.includes('tenant') || role.includes('resident')) return <Navigate to="/tenant" replace />;
    if (role.includes('admin')) return <Navigate to="/admin" replace />;
  }
  
  return <>{children}</>;
};

// App Router
const AppRouter: React.FC = () => {
  return (
    <>
      <NavigationInjector />
      <Routes>
      {/* Auth Routes (no layout) */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Main App Routes (with layout) */}
      <Route
        path="/"
        element={
          <BootRedirect>
            <MainLayout>
              <HomePage />
            </MainLayout>
          </BootRedirect>
        }
      />
      <Route
        path="/map"
        element={
          <MainLayout>
            <MapPage />
          </MainLayout>
        }
      />
      <Route
        path="/search"
        element={
          <MainLayout>
            <SearchPage />
          </MainLayout>
        }
      />
      <Route
        path="/feed"
        element={
          <MainLayout>
            <FeedPage />
          </MainLayout>
        }
      />
      <Route
        path="/reels"
        element={
          <MainLayout>
            <ReelsPage />
          </MainLayout>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MessagesPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProfilePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SettingsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/pricing"
        element={
          <MainLayout>
            <PricingPage />
          </MainLayout>
        }
      />
      <Route
        path="/subscriptions"
        element={<Navigate to="/pricing" replace />}
      />

      {/* Business Routes */}
      <Route
        path="/business"
        element={
          <ProtectedRoute requiredPortal="business">
            <BusinessSelectionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/business/register"
        element={
          <ProtectedRoute requiredPortal="business">
            <BusinessRegistrationPage />
          </ProtectedRoute>
        }
      />
      {/* Business hub — mirrors mobile BusinessProfileScreen */}
      <Route
        path="/business/:businessId"
        element={
          <ProtectedRoute requiredPortal="business">
            <BusinessDashboard />
          </ProtectedRoute>
        }
      />

      {/* Business Majengo Profile Edit */}
      <Route
        path="/business/:businessId/edit"
        element={
          <ProtectedRoute requiredPortal="business">
            <EditBusinessProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Business Core Details Edit */}
      <Route
        path="/business/:businessId/details"
        element={
          <ProtectedRoute requiredPortal="business">
            <BusinessDetailsPage />
          </ProtectedRoute>
        }
      />

      {/* Business sub-pages */}
      <Route
        path="/business/:businessId/products"
        element={<ProtectedRoute requiredPortal="business"><ProductList /></ProtectedRoute>}
      />
      <Route
        path="/business/:businessId/products/new"
        element={<ProtectedRoute requiredPortal="business"><ProductForm /></ProtectedRoute>}
      />
      <Route
        path="/business/:businessId/products/edit/:productId"
        element={<ProtectedRoute requiredPortal="business"><ProductForm /></ProtectedRoute>}
      />
      <Route
        path="/business/:businessId/products/:productId"
        element={<ProtectedRoute requiredPortal="business"><ProductDetails /></ProtectedRoute>}
      />
      <Route
        path="/business/:businessId/services"
        element={<ProtectedRoute requiredPortal="business"><ServiceList /></ProtectedRoute>}
      />
      <Route
        path="/business/:businessId/services/new"
        element={<ProtectedRoute requiredPortal="business"><ServiceForm /></ProtectedRoute>}
      />
      <Route
        path="/business/:businessId/services/edit/:serviceId"
        element={<ProtectedRoute requiredPortal="business"><ServiceForm /></ProtectedRoute>}
      />
      <Route path="/business/:businessId/mpesa" element={<ProtectedRoute requiredPortal="business"><MpesaDashboard /></ProtectedRoute>} />
      <Route path="/business/:businessId/mpesa/setup" element={<ProtectedRoute requiredPortal="business"><MpesaSetupPage /></ProtectedRoute>} />
      <Route path="/business/:businessId/staff" element={<ProtectedRoute requiredPortal="business"><StaffPage /></ProtectedRoute>} />
      <Route path="/business/:businessId/cart" element={<ProtectedRoute requiredPortal="business"><PosCartPage /></ProtectedRoute>} />
      <Route path="/business/:businessId/pos" element={<ProtectedRoute requiredPortal="business"><LivePOSPage /></ProtectedRoute>} />
      <Route path="/business/:businessId/barcodes" element={<ProtectedRoute requiredPortal="business"><BarcodeManager /></ProtectedRoute>} />
      <Route path="/business/:businessId/reports" element={<ProtectedRoute requiredPortal="business"><ReportsPage /></ProtectedRoute>} />
      <Route path="/business/:businessId/orders" element={<ProtectedRoute requiredPortal="business"><BusinessOrdersPage /></ProtectedRoute>} />
      <Route path="/business/:businessId/inventory" element={<ProtectedRoute requiredPortal="business"><InventoryManagementPage /></ProtectedRoute>} />
      <Route path="/business/:businessId/chats" element={<ProtectedRoute requiredPortal="business"><BusinessChatListPage /></ProtectedRoute>} />
      <Route path="/business/:businessId/payments" element={<ProtectedRoute requiredPortal="business"><PaymentsPage /></ProtectedRoute>} />
      <Route path="/business/:businessId/reviews" element={<ProtectedRoute requiredPortal="business"><CustomerReviewsPage /></ProtectedRoute>} />
      <Route path="/business/:businessId/sales" element={<ProtectedRoute requiredPortal="business"><SalesHistoryPage /></ProtectedRoute>} />
      <Route path="/business/:businessId/sales/new" element={<ProtectedRoute requiredPortal="business"><RecordSalePage /></ProtectedRoute>} />
      <Route path="/business/:businessId/hours" element={<ProtectedRoute requiredPortal="business"><BusinessHoursPage /></ProtectedRoute>} />
      <Route path="/business/:businessId/bookings" element={<ProtectedRoute requiredPortal="business"><BookingsPage /></ProtectedRoute>} />
      <Route path="/business/:businessId/bookings/:bookingId" element={<ProtectedRoute requiredPortal="business"><BookingDetails /></ProtectedRoute>} />
      <Route path="/business/:businessId/bookings/new" element={<ProtectedRoute requiredPortal="business"><NewBooking /></ProtectedRoute>} />
      <Route path="/business/:businessId/services/:serviceId" element={<ProtectedRoute requiredPortal="business"><ServiceDetails /></ProtectedRoute>} />
      <Route path="/business/:businessId/promotions" element={<ProtectedRoute requiredPortal="business"><PromotionsPage /></ProtectedRoute>} />
      <Route path="/business/:businessId/commissions" element={<ProtectedRoute requiredPortal="business"><CommissionApproval /></ProtectedRoute>} />
      <Route path="/business/:businessId/commission-payouts" element={<ProtectedRoute requiredPortal="business"><CommissionPayout /></ProtectedRoute>} />
      <Route path="/business/:businessId/posts" element={<ProtectedRoute requiredPortal="business"><BusinessPosts /></ProtectedRoute>} />
      <Route path="/business/:businessId/posts/:postId" element={<ProtectedRoute requiredPortal="business"><BusinessPostDetail /></ProtectedRoute>} />
      <Route path="/business/:businessId/posts/offer" element={<ProtectedRoute requiredPortal="business"><PostOffer /></ProtectedRoute>} />
      <Route path="/business/:businessId/products/:productId/journey" element={<ProtectedRoute requiredPortal="business"><ProductJourney /></ProtectedRoute>} />
      <Route path="/business/:businessId/commission-tasks" element={<ProtectedRoute requiredPortal="business"><CommissionTasks /></ProtectedRoute>} />
      <Route path="/business/:businessId/notifications" element={<ProtectedRoute requiredPortal="business"><NotificationsPage /></ProtectedRoute>} />
      <Route path="/business/:businessId/search" element={<ProtectedRoute requiredPortal="business"><AdvancedSearchPage /></ProtectedRoute>} />
      <Route path="/business/:businessId/payment-links" element={<ProtectedRoute requiredPortal="business"><PaymentLinksPage /></ProtectedRoute>} />

      {/* Public Routes (no auth required) */}
      <Route path="/business/:businessId/public" element={<PublicProfile />} />

      {/* Cart Route (authenticated users) */}
      <Route
        path="/cart"
        element={
          <ProtectedRoute>
            <CartPage />
          </ProtectedRoute>
        }
      />

      {/* Landlord Portal - Protected */}
      <Route
        path="/landlord/*"
        element={
          <ProtectedRoute allowedRoles={['Landlord']}>
            <LandlordLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<PropertyList />} />
        <Route path="dashboard" element={<LandlordDashboard />} />
        <Route path="properties" element={<PropertyList />} />
        <Route path="properties/:propertyId" element={<PropertyDetails />} />
        <Route path="properties/new" element={<PropertyForm />} />
        <Route path="properties/edit/:propertyId" element={<EditProperty />} />
        <Route path="units" element={<AllUnits />} />
        <Route path="units/:propertyId" element={<ManageUnits />} />
        <Route path="units/:propertyId/:propertyName" element={<ManageUnits />} />
        <Route path="units/detail/:propertyId/:unitId" element={<UnitDetails />} />

        <Route path="tenants" element={<TenantList />} />
        <Route path="tenants/:tenantId" element={<TenantDetails />} />
        <Route path="tenants/register" element={<RegisterTenant />} />
        <Route path="tenants/register/:propertyId" element={<RegisterTenant />} />
        <Route path="financials" element={<Financials />} />
        <Route path="financials/new-invoice" element={<CreateInvoice />} />
        <Route path="financials/record-payment" element={<RecordPayment />} />
        <Route path="financials/automated-billing" element={<AutomatedBilling />} />
        <Route path="maintenance" element={<MaintenanceList />} />
        <Route path="payment-methods" element={<ManagePaymentMethods />} />
        <Route path="financials/tuma-setup" element={<TumaSetup />} />
        <Route path="billing/schedules" element={<ManageBillingSchedules />} />
        <Route path="billing/schedules/new" element={<CreateBillingSchedule />} />
        <Route path="units/register" element={<UnitRegistration />} />

        {/* New Routes matching Mobile App */}
        <Route path="bookings" element={<LandlordBookings />} />
        <Route path="vacancies" element={<VacancyList />} />
        <Route path="vacancies/new" element={<PostVacancy />} />
        <Route path="vacancies/preview" element={<VacancyPreview />} />
        <Route path="vacancies/edit/:vacancyId" element={<EditVacancy />} />
        <Route path="reports" element={<LandlordReport />} />
        <Route path="portfolio" element={<PortfolioOverview />} />
        <Route path="documents" element={<DocumentManager />} />
        <Route path="utilities" element={<UtilitiesManager />} />
        <Route path="staff" element={<LandlordStaff />} />
        <Route path="staff/new" element={<AddStaff />} />
        <Route path="staff/:staffId" element={<StaffDetails />} />
        <Route path="staff/:staffId/edit" element={<EditStaff />} />
        <Route path="staff/:staffId/permissions" element={<StaffPermissions />} />
        <Route path="staff/property-management" element={<PropertyStaffManagement />} />

        {/* New Analytics & Reports Routes */}
        <Route path="reports/performance" element={<PropertyPerformance />} />
        <Route path="reports/occupancy" element={<OccupancyAnalytics />} />

        {/* Maintenance & Booking Details Routes */}
        <Route path="maintenance/:requestId" element={<MaintenanceDetails />} />
        <Route path="bookings/:bookingId" element={<LandlordBookingDetails />} />

        {/* Capital Projects & Renovations Routes */}
        <Route path="projects" element={<ProjectList />} />
        <Route path="projects/new" element={<CreateProject />} />
        <Route path="projects/:projectId" element={<ProjectDetails />} />
        <Route path="projects/edit/:projectId" element={<EditProject />} />
      </Route>

      {/* Tenant Portal - Protected */}
      <Route
        path="/tenant"
        element={
          <ProtectedRoute allowedRoles={['Tenant']}>
            <TenantLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<TenantDashboard />} />
        <Route path="profile" element={<TenantProfile />} />
        <Route path="payments" element={<MyPayments />} />
        <Route path="maintenance" element={<MyMaintenance />} />
        <Route path="documents" element={<MyDocuments />} />
        <Route path="select-tenancy" element={<TenancySelector />} />
      </Route>

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
};

// Main App Component
function App() {
  const [showSplash, setShowSplash] = React.useState(true);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            {showSplash ? (
              <SplashScreen onComplete={() => setShowSplash(false)} />
            ) : (
              <AppRouter />
            )}
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
